// frontend/src/components/AlertFlyout.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-23
// Updated:      2026-09-10 09:38 BST
//
// Purpose:
//               Displays the active Vision Systems alert flyout.
//
// Role:
//               Presents prioritised active alerts from AlertContext and allows
//               individual alerts to be dismissed without closing the flyout.
//
// Lifecycle Status:
//               Active production component.
//
// Change Summary (2026-09-10):
//               • Retained the existing AlertFlyout structure and interaction model.
//               • Added narrow-screen positioning so the fixed flyout cannot overflow
//                 the viewport on smaller phones.
//               • Added dynamic viewport height handling using 100dvh on mobile.
//               • Added touch scrolling and overscroll containment for long alert lists.
//               • Increased dismiss-button touch target to 44px on mobile while retaining
//                 the existing compact desktop dismiss control.
//               • Hardened long alert titles/messages so they wrap instead of overflowing.
//
// Change Summary:
//               • Prevented flyout interactions from bubbling to parent controls.
//               • Dismissing an individual alert no longer closes the flyout.
//               • Flyout remains open while additional active alerts remain.
//               • Flyout automatically closes when the final active alert is
//                 dismissed.
//               • Retained click-away behaviour for intentionally closing the
//                 flyout.
//               • Replaced clickable dismiss icon with an accessible button.
//               • Preserved optimistic dismissal and backend synchronisation.
//               • Removed duplicate direct API dismissal request; AlertContext
//                 remains the authoritative owner of alert persistence.
//
// Previous Updates:
//
// Update (2026-01-16):
//               • Uses shared AlertContext so AlertFlyout and AlertWidget cannot
//                 drift independently.
//
// Future Intent:
//               Continue using AlertContext as the authoritative client-side
//               alert state. Flyout visibility should remain user-controlled
//               except when no dismissible alerts remain.
//
// Operational Notes:
//               • AlertContext remains authoritative for alert state and persistence.
//               • Click-away handling remains based on the existing mousedown listener.
//               • No alert sorting, dismissal, persistence or lifecycle logic is changed
//                 by the mobile hardening pass.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  FaExclamationCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBell,
  FaTimes,
} from "react-icons/fa";

import { useAlerts } from "../context/AlertContext";

const AlertFlyout = ({ onClose }) => {
  const flyoutRef = useRef(null);
  const previousAlertCountRef = useRef(null);

  const { alerts, dismissAlert } = useAlerts();

  const alertStyles = useMemo(
    () => ({
      critical: {
        bg: "#FAEAE8",
        border: "#C9190B",
        icon: "#C9190B",
        text: "#7D1007",
        close: "#7D1007",
        iconEl: <FaExclamationCircle />,
      },

      warning: {
        bg: "#FDF7E7",
        border: "#F0AB00",
        icon: "#F0AB00",
        text: "#866618",
        close: "#866618",
        iconEl: <FaExclamationTriangle />,
      },

      success: {
        bg: "#F3FAF2",
        border: "#3E8635",
        icon: "#3E8635",
        text: "#1E4F18",
        close: "#1E4F18",
        iconEl: <FaCheckCircle />,
      },

      info: {
        bg: "#E7F1FA",
        border: "#2B9AF3",
        icon: "#2B9AF3",
        text: "#024469",
        close: "#024469",
        iconEl: <FaBell />,
      },
    }),
    []
  );

  const priority = useMemo(
    () => ({
      critical: 1,
      warning: 2,
      success: 3,
      info: 4,
    }),
    []
  );

  const sortedAlerts = useMemo(() => {
    const alertArray = Array.isArray(alerts) ? alerts : [];

    return [...alertArray].sort(
      (a, b) =>
        (priority[a?.severity] || 999) -
        (priority[b?.severity] || 999)
    );
  }, [alerts, priority]);

  /*
   * Click-away handling.
   *
   * Only interactions genuinely outside the flyout should close it.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        flyoutRef.current &&
        !flyoutRef.current.contains(event.target)
      ) {
        if (typeof onClose === "function") {
          onClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  /*
   * Close automatically only when the flyout previously contained alerts
   * and the final alert has now been removed.
   *
   * This avoids closing the flyout merely because it initially renders
   * before alerts have loaded.
   */
  useEffect(() => {
    const currentAlertCount = sortedAlerts.length;
    const previousAlertCount = previousAlertCountRef.current;

    if (
      previousAlertCount !== null &&
      previousAlertCount > 0 &&
      currentAlertCount === 0
    ) {
      if (typeof onClose === "function") {
        onClose();
      }
    }

    previousAlertCountRef.current = currentAlertCount;
  }, [sortedAlerts.length, onClose]);

  /*
   * Stop events originating inside the flyout from bubbling to the
   * notification/bell control which may own the flyout visibility state.
   */
  const stopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleDismiss = useCallback(
    (event, id) => {
      /*
       * Prevent the dismiss interaction from reaching a parent notification
       * control and toggling the entire flyout closed.
       */
      event.preventDefault();
      event.stopPropagation();

      /*
       * AlertContext owns both the optimistic UI update and backend
       * synchronisation for alert dismissal.
       */
      dismissAlert(id);
    },
    [dismissAlert]
  );

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .vx-alert-flyout {
            top: 76px !important;
            right: 12px !important;
            left: 12px !important;
            width: auto !important;
            max-height: calc(100dvh - 92px) !important;
            padding: 12px !important;
            overscroll-behavior: contain;
            -webkit-overflow-scrolling: touch;
          }

          .vx-alert-flyout-dismiss {
            width: 44px !important;
            height: 44px !important;
            top: 4px !important;
            right: 4px !important;
          }

          .vx-alert-flyout-copy {
            padding-right: 2.75rem !important;
          }
        }
      `}</style>

      <div
        ref={flyoutRef}
        className="vx-alert-flyout"
        onMouseDown={stopPropagation}
        onClick={stopPropagation}
        style={{
          position: "fixed",

          top: "95px",
          right: "5.5rem",

          zIndex: 9999,

          width: "360px",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "480px",

          overflowY: "auto",
          overflowX: "hidden",

          backgroundColor: "#fff",

          borderRadius: "12px",

          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",

          padding: "1rem",

          display: "flex",
          flexDirection: "column",

          gap: "0.75rem",

          fontFamily: "var(--font-body)",

          boxSizing: "border-box",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {sortedAlerts.map((alert) => {
          const style =
            alertStyles[alert.severity] ||
            alertStyles.info;

          return (
            <div
              key={alert.id}
              style={{
                backgroundColor: style.bg,

                borderTop: `2px solid ${style.border}`,

                borderRadius: "8px",

                padding: "0.75rem",

                display: "flex",
                alignItems: "flex-start",

                position: "relative",

                gap: "0.75rem",

                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  color: style.icon,
                  fontSize: "1.25rem",
                  flexShrink: 0,
                }}
              >
                {style.iconEl}
              </div>

              <div
                className="vx-alert-flyout-copy"
                style={{
                  flexGrow: 1,
                  minWidth: 0,
                  paddingRight: "1.25rem",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    color: style.text,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {alert.title}
                </div>

                {alert.message ? (
                  <div
                    style={{
                      color: style.text,
                      fontSize: "0.85rem",
                      marginTop: 4,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {alert.message}
                  </div>
                ) : null}
              </div>

              <button
                type="button"
                className="vx-alert-flyout-dismiss"
                title="Dismiss"
                aria-label={`Dismiss ${alert.title || "alert"}`}
                onMouseDown={stopPropagation}
                onClick={(event) =>
                  handleDismiss(event, alert.id)
                }
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  width: "24px",
                  height: "24px",

                  padding: 0,

                  border: "none",
                  borderRadius: "4px",

                  background: "transparent",

                  cursor: "pointer",

                  color: style.close,

                  opacity: 0.8,

                  fontFamily: "inherit",
                  touchAction: "manipulation",
                }}
              >
                <FaTimes size={14} />
              </button>
            </div>
          );
        })}

        {sortedAlerts.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              fontSize: "0.9rem",
              color: "#666",
            }}
          >
            No active alerts
          </div>
        ) : null}
      </div>
    </>
  );
};

export default AlertFlyout;
